<template>
  <component :is="label ? 'b-form-group' : 'div'" :label="label" :label-for="id" :class="[{ 'validation-required': isRequired }, classGroup]">
    <b-overlay :show="overlay">
      <b-input-group>
        <feather-icon v-if="showIcon" icon="CalendarIcon" size="22" class="text-primary m-auto px-1" @click="openWithIcon" />
        <flat-pickr
          :id="id"
          :class="[
            'form-control rounded-left',
            clearable && !disabled ? 'rounded-right-0' : 'rounded-right',
            { 'is-valid': stateComputed === true, 'is-invalid': stateComputed === false }
          ]"
          :name="name || label"
          :model-value="value"
          :placeholder="placeholder !== false ? placeholder || label : ''"
          :required="isRequired"
          :disabled="disabled"
          :key="key"
          :config="{
            ...config,
            altInput: altInputComputed,
            allowInput: allowInputComputed,
            locale: $i18n.locale,
            time_24hr: time_24hr,
            disableMobile: true,

            maxDate: maxComputed,
            minDate: minComputed,
            noCalendar: noCalendar,
            dateFormat: dateFormatComputed,
            altFormat: altFormatComputed,
            enableTime: enableTime,
            disable: disable,
            onOpen: onOpen,
            onClose: onCloseMarque,
            onMonthChange: onMonthChange,
            onChange: onChange,
            onValueUpdate: onValueUpdate,
          }"
          v-bind="$attrs"
        />
        <b-input-group-append>
          <button v-if="clearable && !disabled" class="btn btn-sm btn-outline-primary text-danger" type="button" @click="value = null">
            <feather-icon icon="XIcon" />
          </button>
        </b-input-group-append>
      </b-input-group>
      <span v-if="!hideErrors && errors[0]" class="text-danger">{{ deleteFieldText(errors[0]) }}</span>
      <slot />
    </b-overlay>
  </component>
</template>

<script>
import { defineComponent, computed } from 'vue'
import { icon } from '@fortawesome/fontawesome-svg-core';

export default defineComponent({
  name: 'AppDatepicker',
  inheritAttrs: false,
})
</script>

<script setup>
import { useFlatpickr, marquerCalendrierAncre } from '@/composables/useFlatpickr'
import { useField } from 'vee-validate'
import {ref, watch} from "vue";
import { useI18n } from 'vue-i18n'

const { config } = useFlatpickr()

const props = defineProps({
  modelValue: { type: [Number, String, Date], default: undefined },
  id: { type: String, default: '' },
  name: { type: String, default: '' },
  vvName: { type: String, default: undefined },
  classGroup: { type: String, default: undefined },
  label: { type: String, default: '' },
  placeholder: { type: String, default: undefined },
  overlay: { type: Boolean, default: false },
  rules: { type: String, default: '' },
  required: { type: Boolean, default: false },
  hideErrors: { type: Boolean, default: false },
  state: { type: Boolean, default: undefined },
  max: { type: [String, Number, Date], default: undefined },
  min: { type: [String, Number, Date], default: undefined },
  noCalendar: { type: Boolean, default: false },
  enableTime: { type: Boolean, default: false },
  dateFormat: { type: String, default: undefined },
  altFormat: { type: String, default: undefined },
  disabled: { type: Boolean, default: false },
  disable: { type: Array, default: () => [] },
  // eslint-disable-next-line vue/prop-name-casing
  time_24hr: { type: Boolean, default: true },
  clearable: { type: Boolean, default: false },
  otherSelectedDate: { type: [String, Number, Date], default: undefined },
  showIcon: { type: Boolean, default: false },
})

const { locale } = useI18n()

const isRequired = computed(() => props.rules.includes('required') || props.required)

// MFC2-681 NOT OK 24 — le format de date du module porte le suffixe +00:00 en litteral : la valeur
// du champ est donc lue comme une heure murale, 12:00 UTC s'affichant et se comparant comme 12:00.
// Les bornes doivent etre lues de la meme facon. Converties en instant reel, elles se retrouvaient
// decalees du fuseau local : une borne minimale egale a la valeur devenait une heure plus tard
// qu'elle, la valeur tombait hors des bornes, et le module la rejette en vidant son champ. C'est ce
// qui vidait la date de fin d'un hangar, dont la borne minimale est sa date de debut. Toute paire
// de champs bornee l'un par l'autre est concernee, les dates de la carte Filtres des commandes
// comprises ; le volet hangar le rendait systematique parce que sa date de fin part egale a sa
// date de debut, donc pile sur la borne.
// Une chaine sans suffixe garde son ancienne lecture, elle est deja en heure locale.
const toLocalDate = (val) => {
  if (!val || val === 'Invalid date') return undefined
  if (val instanceof Date) return val
  if (typeof val === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return new Date(val + 'T00:00:00')
    const murale = val.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}(?::\d{2})?)(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})$/)
    if (murale) return new Date(`${murale[1]}T${murale[2].length === 5 ? `${murale[2]}:00` : murale[2]}`)
  }
  return new Date(val)
}

const maxComputed = computed(() => toLocalDate(props.max))

const minComputed = computed(() => toLocalDate(props.min))

const computedRules = computed(() => {
  let normalizeRules = props.rules
  if (isRequired.value && !normalizeRules.includes('required')) {
    if (!normalizeRules) normalizeRules = 'required'
    else normalizeRules += '|required'
  }
  return normalizeRules
})

const { errors, value, validate } = useField(() => props.id, computedRules, {
  syncVModel: true,
})

const stateComputed = computed(() => {
  if (errors[0]) return false
  return props.state
})

const dateFormatComputed = computed(() => {
  if (props.dateFormat) return props.dateFormat

  // Dans le cas où on a un timepicker (sans calendrier) on ne veut pas de date
  if (props.enableTime && props.noCalendar) return 'H:i'

  return 'Y-m-dTH:i:S+00:00'
})

const altFormatComputed = computed(() => {
  if (props.altFormat) return props.altFormat

  // Dans le cas où on a un timepicker (sans calendrier) on ne veut pas de date
  if (props.enableTime && props.noCalendar) return null

  // Dans le cas où l'on a pas de timepicker on ne veut pas d'heure
  if (!props.enableTime) return 'D, M J Y'

  return 'D, M J Y H:i'
})

// Le alt input ne peut être défini en mode timepicker seulement pour cause d'une erreur sur le parsing de la date
const altInputComputed = computed(() => !(props.enableTime && props.noCalendar))

const allowInputComputed = computed(() => false)

// Trick to prevent 'The {field} field must be ...'
// But bad solution about langs, I suppose...
// vvName with default to ' ' seems work, but break the validator
const deleteFieldText = msg => {
  const reg = new RegExp(props.id, 'i')
  if (!props.vvName) return msg.replace(reg, '')
  if (props.vvName === true) return msg.replace(reg, props.label || '')
  return msg.replace(reg, props.vvName)
}

const calculateOtherSelectedDate = (selectedDates, dateStr, instance) => {
  if (props.otherSelectedDate) {
    const otherSelectedDate = toLocalDate(props.otherSelectedDate)
    if (otherSelectedDate) {
      if (instance) {
        const dayContainer = instance.calendarContainer.querySelector('.dayContainer')
        if (dayContainer) {
          const days = dayContainer.querySelectorAll('.flatpickr-day')

          for (const day of days) {
            if (day.dateObj.getDate() === otherSelectedDate.getDate() && day.dateObj.getMonth() === otherSelectedDate.getMonth() && day.dateObj.getFullYear() === otherSelectedDate.getFullYear()) {
              day.classList.add('other-selected-day')
              break
            }
          }
        }
      }
    }
  }
}

const openWithIcon = () => {
  const input = document.getElementById(props.id)
  if (input) input._flatpickr.open()
}

// MFC2-681 NOT OK 33 — tant qu un calendrier ancre est ouvert, il recouvre les commandes du
// formulaire : elles s annoncent inactives par une marque posee sur le corps du document.
const onOpen = (selectedDates, dateStr, instance) => {
  calculateOtherSelectedDate(selectedDates, dateStr, instance)
  marquerCalendrierAncre(instance, true)
}

const onCloseMarque = (selectedDates, dateStr, instance) => marquerCalendrierAncre(instance, false)
const onMonthChange = calculateOtherSelectedDate
const onChange = calculateOtherSelectedDate
const onValueUpdate = calculateOtherSelectedDate

const key = ref(0)

watch(locale, newLocale => {
  if (newLocale) {
    key.value++
    validate()
  }
})

</script>

<style>
/* MFC2-681 NOT OK 22 — en mode ancre, le module calendrier enveloppe le champ dans un bloc de son
   cru et y range le calendrier. Ce bloc devient l'element souple du groupe de champs : sans ces
   regles il se reduit a la largeur naturelle du champ et le bouton d'effacement se decolle. */
.input-group > .flatpickr-wrapper {
  display: block;
  flex: 1 1 auto;
  min-width: 0;
}
.input-group > .flatpickr-wrapper > .form-control,
.input-group > .flatpickr-wrapper > input {
  width: 100%;
}
/* Le calendrier ancre se superpose au contenu qui le suit, il ne le pousse pas. */
.flatpickr-calendar.static.open {
  z-index: 1056; /* au-dessus du volet lateral de Bootstrap */
}

/* MFC2-681 NOT OK 33 — les commandes du formulaire passent sous le calendrier ancre. Tant qu il
   est ouvert, elles s annoncent inactives et ne repondent plus a l appui : le geste qui ferme le
   calendrier ne doit rien declencher d autre. Le clic que ce geste produit arrive apres la
   fermeture, donc apres le retour de ces regles : c est le garde pose dans useFlatpickr qui
   l avale. Les deux vont ensemble, l un pour ce qui se voit, l autre pour ce qui se passe. */
body.calendrier-ancre-ouvert form .btn {
  pointer-events: none;
  opacity: 0.5;
}

.flatpickr-calendar {
  border-color: #DCC181;
  border: 1px solid #DCC181;
}

.other-selected-day:not(.selected) {
  background-color: #e3e3e3 !important;
  color: #4b4b4b !important;
}

.flatpickr-input[readonly] {
  background-color: inherit !important;
}

.flatpickr-disabled {
  background-color: #f6f6f6 !important;
}

.flatpickr-disabled {
  background-color: #f6f6f6 !important;
  color: #4b4b4b !important;

  &:not(.today) {
    border-radius: 0;
  }
}
.flatpickr-calendar .flatpickr-day {
  min-width: 43.98px; /* Dimension exacte des en-têtes du calendrier */
}
.dayContainer {
  -webkit-justify-content: center !important;
  justify-content: center !important;
}
.flatpickr-calendar.hasTime.noCalendar {
  width: 250px !important;
}
</style>
